import { useState, useEffect } from 'react';
import { FaLinkedin } from 'react-icons/fa';
import { CiGlobe } from 'react-icons/ci';
import { SiSalesforce } from 'react-icons/si';
import { supabase } from '../supabaseClient';
import '../styles/DashboardLeads.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Helper to check if a LinkedIn URL is valid
const isValidLinkedInUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const lower = url.toLowerCase();
  return lower.startsWith('http') && lower.includes('linkedin.com');
};

// Helper to check if a URL is valid
const isValidUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return url.toLowerCase().startsWith('http');
};

function DashboardLeads({ searches, user }) {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [salesforceConnected, setSalesforceConnected] = useState(false);
  const [syncingLeads, setSyncingLeads] = useState({}); // Track which leads are being synced
  const [syncedLeads, setSyncedLeads] = useState({}); // Track which leads have been synced

  // Check Salesforce connection status and fetch synced opportunities
  useEffect(() => {
    const checkSalesforceStatus = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('Users')
          .select('salesforce_connected, salesforce_sync_mode')
          .eq('id', user.id)
          .single();

        if (!error && data) {
          setSalesforceConnected(data.salesforce_connected || false);

          // If connected, fetch existing synced opportunities from Salesforce
          if (data.salesforce_connected) {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
              try {
                const response = await fetch(`${BACKEND_URL}/salesforce/synced-opportunities`, {
                  headers: {
                    'Authorization': `Bearer ${session.access_token}`
                  }
                });
                if (response.ok) {
                  const result = await response.json();
                  // result.opportunities is { company_name_lowercase: opportunity_url }
                  setSyncedLeads(result.opportunities || {});
                }
              } catch (fetchErr) {
                console.error('Error fetching synced opportunities:', fetchErr);
              }
            }
          }
        }
      } catch (err) {
        console.error('Error checking Salesforce status:', err);
      }
    };

    checkSalesforceStatus();
  }, [user]);

  // Extract all leads from all searches
  useEffect(() => {
    if (!searches || searches.length === 0) {
      setLeads([]);
      setFilteredLeads([]);
      return;
    }

    // Collect all leads from all completed searches
    const allLeads = [];
    const seenCompanies = new Set();

    searches.forEach(search => {
      if (search.status === 'complete' && search.results) {
        // Handle results that might be a JSON string
        let results = search.results;
        if (typeof results === 'string') {
          try {
            results = JSON.parse(results);
          } catch (e) {
            console.error('Error parsing results:', e);
            return;
          }
        }

        // Ensure results is an array
        if (!Array.isArray(results)) {
          return;
        }

        results.forEach(result => {
          // Dedupe by company name
          const companyKey = (result.company_name || '').toLowerCase();
          if (companyKey && !seenCompanies.has(companyKey)) {
            seenCompanies.add(companyKey);
            allLeads.push({
              ...result,
              searchQuery: search.query,
              searchDate: search.created_at
            });
          }
        });
      }
    });

    setLeads(allLeads);
    setFilteredLeads(allLeads);
  }, [searches]);

  // Filter leads based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredLeads(leads);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = leads.filter(lead => {
      const companyName = (lead.company_name || '').toLowerCase();
      const companyDesc = (lead.company_description || '').toLowerCase();
      const fitReason = (lead.fit_reason || '').toLowerCase();
      const founderNames = (lead.founders || []).map(f => (f.name || '').toLowerCase()).join(' ');

      return companyName.includes(query) ||
             companyDesc.includes(query) ||
             fitReason.includes(query) ||
             founderNames.includes(query);
    });

    setFilteredLeads(filtered);
  }, [searchQuery, leads]);

  // Sync lead to Salesforce
  const handleSyncToSalesforce = async (lead) => {
    const companyKey = (lead.company_name || '').toLowerCase();
    if (syncingLeads[companyKey] || syncedLeads[companyKey]) return;

    setSyncingLeads(prev => ({ ...prev, [companyKey]: true }));

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${BACKEND_URL}/salesforce/sync-lead`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          company_name: lead.company_name,
          company_description: lead.company_description,
          founders: lead.founders,
          fit_reason: lead.fit_reason,
          search_query: lead.searchQuery
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to sync');
      }

      const result = await response.json();
      setSyncedLeads(prev => ({ ...prev, [companyKey]: result.opportunity_url }));
    } catch (err) {
      console.error('Error syncing to Salesforce:', err);
      alert(`Failed to sync: ${err.message}`);
    } finally {
      setSyncingLeads(prev => ({ ...prev, [companyKey]: false }));
    }
  };

  const renderLeadCard = (lead, index) => {
    const companyKey = (lead.company_name || '').toLowerCase();
    return (
      <div key={`${lead.company_name}-${index}`} className="lead-card">
        <div className="lead-card-company">
          <div className="lead-company-name-row">
            <span className="lead-company-name">{lead.company_name || 'Unknown Company'}</span>
            {isValidUrl(lead.company_url) && (
              <a
                href={lead.company_url}
                target="_blank"
                rel="noopener noreferrer"
                className="company-link-pill"
                title="Visit company website"
              >
                <CiGlobe size={14} />
                <span>Read More</span>
              </a>
            )}
          </div>
          {lead.company_description && (
            <span className="lead-description">{lead.company_description}</span>
          )}
        </div>

        {lead.founders && lead.founders.length > 0 && (
          <div className="lead-card-section">
            <span className="lead-card-label">Founders</span>
            <div className="lead-founders-list">
              {lead.founders.map((founder, idx) => (
                <span key={idx} className="lead-founder">
                  {founder.name}
                  {isValidLinkedInUrl(founder.linkedin) && (
                    <a
                      href={founder.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="lead-founder-linkedin"
                      title={`${founder.name}'s LinkedIn`}
                    >
                      <FaLinkedin size={12} color="#0077b5" />
                    </a>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {lead.fit_reason && (
          <div className="lead-card-section">
            <span className="lead-card-label">Fit Reason</span>
            <p className="lead-card-fit">{lead.fit_reason}</p>
          </div>
        )}

        <div className="lead-card-footer">
          <span className="lead-card-source">
            {lead.searchQuery?.substring(0, 30)}{lead.searchQuery?.length > 30 ? '...' : ''}
          </span>
          {salesforceConnected && (
            <div className="lead-salesforce-inline">
              {syncedLeads[companyKey] ? (
                <a
                  href={syncedLeads[companyKey]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="salesforce-synced-link"
                >
                  <SiSalesforce size={14} />
                  View in Salesforce
                </a>
              ) : (
                <button
                  className={`salesforce-sync-btn ${syncingLeads[companyKey] ? 'syncing' : ''}`}
                  onClick={() => handleSyncToSalesforce(lead)}
                  disabled={syncingLeads[companyKey]}
                >
                  <SiSalesforce size={14} />
                  {syncingLeads[companyKey] ? 'Syncing...' : 'Sync'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-leads">
      <div className="leads-header">
        <h1 className="leads-title">Leads</h1>
        <input
          type="text"
          className="leads-search"
          placeholder="Search by company, founder, or description..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {filteredLeads.length === 0 ? (
        <div className="leads-empty">
          <p>{searchQuery ? 'No leads match your search.' : 'No leads found yet. Results will appear here once your searches are complete.'}</p>
        </div>
      ) : (
        <>
          {/* Desktop table view */}
          <div className="leads-table-container">
            <table className="leads-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Founders</th>
                  <th>Fit Reason</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead, index) => {
                  const companyKey = (lead.company_name || '').toLowerCase();
                  return (
                    <tr key={`${lead.company_name}-${index}`}>
                      <td className="lead-company-cell">
                        <div className="lead-company-name-row">
                          <span className="lead-company-name">{lead.company_name || 'Unknown Company'}</span>
                          {isValidUrl(lead.company_url) && (
                            <a
                              href={lead.company_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="company-link-pill"
                              title="Visit company website"
                            >
                              <CiGlobe size={14} />
                              <span>Read More</span>
                            </a>
                          )}
                        </div>
                        {lead.company_description && (
                          <span className="lead-description">{lead.company_description}</span>
                        )}
                        {salesforceConnected && (
                          <div className="lead-salesforce-inline">
                            {syncedLeads[companyKey] ? (
                              <a
                                href={syncedLeads[companyKey]}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="salesforce-synced-link"
                              >
                                <SiSalesforce size={14} />
                                View in Salesforce
                              </a>
                            ) : (
                              <button
                                className={`salesforce-sync-btn ${syncingLeads[companyKey] ? 'syncing' : ''}`}
                                onClick={() => handleSyncToSalesforce(lead)}
                                disabled={syncingLeads[companyKey]}
                              >
                                <SiSalesforce size={14} />
                                {syncingLeads[companyKey] ? 'Syncing...' : 'Sync with Salesforce'}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="lead-founders-cell">
                        {lead.founders && lead.founders.length > 0 ? (
                          <div className="lead-founders-list">
                            {lead.founders.map((founder, idx) => (
                              <span key={idx} className="lead-founder">
                                {founder.name}
                                {isValidLinkedInUrl(founder.linkedin) && (
                                  <a
                                    href={founder.linkedin}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="lead-founder-linkedin"
                                    title={`${founder.name}'s LinkedIn`}
                                  >
                                    <FaLinkedin size={12} color="#0077b5" />
                                  </a>
                                )}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="lead-no-data">-</span>
                        )}
                      </td>
                      <td className="lead-fit-cell">
                        {lead.fit_reason || <span className="lead-no-data">-</span>}
                      </td>
                      <td className="lead-source-cell">
                        {lead.searchQuery?.substring(0, 40)}{lead.searchQuery?.length > 40 ? '...' : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile card view */}
          <div className="leads-cards-container">
            {filteredLeads.map((lead, index) => renderLeadCard(lead, index))}
          </div>
        </>
      )}
    </div>
  );
}

export default DashboardLeads;
